import { authStore } from './auth-store';

export type OAuthError = `oauth_failed` | `unknown_payor` | `payor_mismatch`;
export type OAuthProvider = `google` | `microsoft`;

const DEFAULT_OAUTH_PROVIDER: OAuthProvider = `google`;

export interface OAuthResult {
    token?: string;
    ttl?: string;
    communityShortName?: string;
    provider?: OAuthProvider;
    error?: OAuthError;
}

type StoredOAuthLoginResult =
    | { status: `pending`; redirectPath: string; provider?: OAuthProvider }
    | { status: `success`; redirectPath?: string; provider?: OAuthProvider }
    | { status: `error`; error: OAuthError; provider?: OAuthProvider };

export type OAuthLoginResult =
    | { status: `success`; redirectPath?: string; provider: OAuthProvider }
    | { status: `error`; error: OAuthError; provider: OAuthProvider };

export type AppliedOAuthResult =
    | { status: `success`; provider: OAuthProvider }
    | { status: `error`; provider: OAuthProvider; error: OAuthError };

const OAUTH_LOGIN_RESULT_STORAGE_KEY = `oauthLoginResult`;

function isOAuthError(value: string | null): value is OAuthError {
    return value === `oauth_failed` || value === `unknown_payor` || value === `payor_mismatch`;
}

function isOAuthProvider(value: unknown): value is OAuthProvider {
    return value === `google` || value === `microsoft`;
}

function isStoredOAuthLoginResult(value: unknown): value is StoredOAuthLoginResult {
    if (!value || typeof value !== `object`) return false;

    const result = value as Partial<StoredOAuthLoginResult>;

    if (result.provider !== undefined && !isOAuthProvider(result.provider)) return false;

    if (result.status === `pending`) {
        return typeof result.redirectPath === `string`;
    }

    if (result.status === `success`) {
        return result.redirectPath === undefined || typeof result.redirectPath === `string`;
    }

    if (result.status === `error`) {
        return isOAuthError(result.error ?? null);
    }

    return false;
}

function readOAuthLoginResult(): StoredOAuthLoginResult | null {
    const value = sessionStorage.getItem(OAUTH_LOGIN_RESULT_STORAGE_KEY);
    if (!value) return null;

    try {
        const result = JSON.parse(value);
        if (isStoredOAuthLoginResult(result)) return result;
    } catch {
        // Ignore malformed values and reset the OAuth handoff state below.
    }

    sessionStorage.removeItem(OAUTH_LOGIN_RESULT_STORAGE_KEY);
    return null;
}

function writeOAuthLoginResult(result: StoredOAuthLoginResult): void {
    sessionStorage.setItem(OAUTH_LOGIN_RESULT_STORAGE_KEY, JSON.stringify(result));
}

// Strip OAuth params before analytics/Sentry can capture session ids.
export function extractOAuthResult(): OAuthResult | null {
    const params = new URLSearchParams(window.location.search);
    const token = params.get(`token`);
    const ttl = params.get(`ttl`);
    const communityShortName = params.get(`communityShortName`);
    const error = params.get(`error`);
    const provider = params.get(`provider`);

    if (!token && !error) return null;

    [`token`, `ttl`, `communityShortName`, `error`, `provider`].forEach(key => params.delete(key));
    const newSearch = params.toString();
    window.history.replaceState({}, ``, window.location.pathname + (newSearch ? `?${newSearch}` : ``));

    const resolvedProvider = isOAuthProvider(provider) ? provider : undefined;

    if (token && ttl) {
        return {
            token,
            ttl,
            communityShortName: communityShortName ?? undefined,
            provider: resolvedProvider
        };
    }

    return {
        error: isOAuthError(error) ? error : `oauth_failed`,
        provider: resolvedProvider
    };
}

export function applyOAuthResult(result: OAuthResult): AppliedOAuthResult {
    const pendingResult = readOAuthLoginResult();
    const redirectPath = pendingResult?.status === `pending` ? pendingResult.redirectPath : undefined;
    const provider = result.provider ?? pendingResult?.provider ?? DEFAULT_OAUTH_PROVIDER;

    if (result.token && result.ttl) {
        authStore.setSession(result.token, result.ttl);
        writeOAuthLoginResult({
            status: `success`,
            redirectPath,
            provider
        });
        return { status: `success`, provider };
    }

    const error = result.error ?? `oauth_failed`;
    writeOAuthLoginResult({
        status: `error`,
        error,
        provider
    });
    return { status: `error`, provider, error };
}

export function beginOAuthLogin(provider: OAuthProvider, redirectPath: string): void {
    writeOAuthLoginResult({
        status: `pending`,
        redirectPath,
        provider
    });
}

export function startOAuthLogin(provider: OAuthProvider, redirectPath: string): void {
    const currentSubdomain = window.location.hostname.split(`.`)[0];
    const returnTo = `${currentSubdomain}:${window.location.pathname}`;
    const authUrl = new URL(`${window.settings.SUNBOUNDPAY_API}/payor/auth/${provider}/init`);
    authUrl.searchParams.set(`returnTo`, returnTo);
    // Keep the account picker available after a failed attempt.
    authUrl.searchParams.set(`prompt`, `select_account`);

    beginOAuthLogin(provider, redirectPath);
    window.location.href = authUrl.toString();
}

export function takeOAuthLoginResult(): OAuthLoginResult | null {
    const result = readOAuthLoginResult();
    if (!result || result.status === `pending`) return null;

    sessionStorage.removeItem(OAUTH_LOGIN_RESULT_STORAGE_KEY);
    return { ...result, provider: result.provider ?? DEFAULT_OAUTH_PROVIDER };
}

export function clearOAuthLoginResult(): void {
    sessionStorage.removeItem(OAUTH_LOGIN_RESULT_STORAGE_KEY);
}
